"use client";

import Script from "next/script";
import Link from "next/link";
import { useState } from "react";
import ProductCarousel from "@/components/ProductCarousel";
import ProductInfo from "@/components/ProductInfo";
import PdfVersion from "@/components/PdfVersion";
import Image from "next/image";
import PdfDetailShell from "@/components/summary-pdf/PdfDetailShell";
import CompleteUpsellBanner from "@/components/summary-pdf/CompleteUpsellBanner";

const product = {
  id: "26-3-compressed-pdf",
  title: "[압축]\n2026년 3회\n실기 이론 요약 PDF",
  description:
    "2026년 3회 실기 이론 요약 PDF입니다. 기출 문제 키워드를 기반으로 간결하게 정리하여 효율적인 학습이 가능합니다.",
  price: 10000,
  discountedPrice: 8000,
  orderIdPrefix: "order_",
  images: [
    { src: "/summary-pdf/26-3-compressed/v1-0/8e5b8b63/1.webp", alt: "이론 PDF v1-0 미리보기 1" },
    { src: "/summary-pdf/26-3-compressed/v1-0/8e5b8b63/2.webp", alt: "이론 PDF v1-0 미리보기 2" },
    { src: "/summary-pdf/26-3-compressed/v1-0/8e5b8b63/3.webp", alt: "이론 PDF v1-0 미리보기 3" },
    { src: "/summary-pdf/26-3-compressed/v1-0/8e5b8b63/4.webp", alt: "이론 PDF v1-0 미리보기 4" },
    { src: "/summary-pdf/26-3-compressed/v1-0/8e5b8b63/5.webp", alt: "이론 PDF v1-0 미리보기 5 · 프리픽스와 서브넷 분할" },
    { src: "/summary-pdf/26-3-compressed/v1-0/8e5b8b63/6.webp", alt: "이론 PDF v1-0 미리보기 6" },
    { src: "/summary-pdf/26-3-compressed/v1-0/8e5b8b63/7.webp", alt: "이론 PDF v1-0 미리보기 7" },
  ],
};

export default function CompressedPDFClient() {
  const [isYoutubeSubscriber, setIsYoutubeSubscriber] = useState(false);

  const handleYoutubeVerificationSuccess = () => {
    setIsYoutubeSubscriber(true);
  };

  // 결제 박스(ProductInfo) — 셸이면 우측 sticky 슬롯, 폴백이면 캐러셀 옆. 한 번만 만들어 재사용
  // (결제 핸들러/폴링/모달/콜백/GSI 로직은 ProductInfo 내부 — 위치만 이동, 변경 없음).
  const productInfo = (
    <ProductInfo
      title={product.title}
      price={product.price}
      discountedPrice={product.discountedPrice}
      isYoutubeSubscriber={isYoutubeSubscriber}
      onYoutubeVerificationSuccess={handleYoutubeVerificationSuccess}
      orderIdPrefix={product.orderIdPrefix}
      productId="26-3-compressed-pdf"
      nicepayClientId={process.env.NEXT_PUBLIC_NICEPAY_CLIENT_ID!}
      showYoutubeDiscount={true}
      youtubeChannelId={process.env.NEXT_PUBLIC_YOUTUBE_CHANNEL_ID}
      youtubeClientId={process.env.NEXT_PUBLIC_YOUTUBE_CLIENT_ID}
    />
  );

  // 상품 설명 섹션 본문(감자 캐릭터 + 설명문 + 업데이트 안내 + PdfVersion) — 셸/폴백 공통.
  const overviewInner = (
    <>
      <Image
        src="/summary-pdf/26-1-compressed/3d_compressed-with-djc_gamja.webp"
        alt="압축 감자 캐릭터"
        width={120}
        height={120}
        className="w-[80px] h-auto mx-auto my-4"
      />
      <p className="text-muted-foreground leading-relaxed text-base">
        {product.description}
      </p>
      <p className="text-foreground leading-relaxed text-semibold mt-2 mx-auto text-center">
        가림막(Q)·가로(H) v1-0 버전도 함께 받아보실 수 있어요.
      </p>
      <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
        <p className="mb-2 text-sm font-medium text-amber-900 dark:text-amber-200">
          ✅ 현재 버전 안내 (v1-0)
        </p>
        <p className="text-sm leading-relaxed text-amber-800 dark:text-amber-300">
          2026년 2회까지의 기출과 3회 예상 실기 이론 문제 범위를 반영한 최신본입니다.
        </p>
      </div>
      <div className="mt-4">
        <PdfVersion id="26-3-compressed-pdf" defaultExpanded={true} />
      </div>
    </>
  );

  // 세일즈 상세 블록 — recordBlock·completeBanner 는 폴백(모바일) 본문 전용(셸에선 좌측
  // 사이드바 컴팩트 버전으로 대체). 나머지 블록은 셸/폴백 공통.
  const recordBlock = (
    <div className="break-words bg-purple-50 p-6 rounded-lg border-l-4 border-purple-500 dark:bg-purple-950/40 dark:border-purple-500">
      <h3 className="text-lg font-semibold text-purple-800 mb-4 dark:text-purple-300">
        🏆 정처기 감자 실기 이론 압축 요약 PDF 실적
      </h3>
      <div className="space-y-2 text-gray-700 dark:text-gray-300">
        <p className="flex items-center">
          <span aria-hidden="true" className="text-purple-500 mr-2 dark:text-purple-400">
            ✓
          </span>
          <span>
            <strong>25년 2회</strong> 이론 11문제 중{" "}
            <strong className="text-purple-700 dark:text-purple-300">9문제</strong>
          </span>
        </p>
        <Link
          href="/past-exam/2025/2025-3-review"
          aria-label="25년 3회 이론 12문제 중 9문제 적중 — 적중 상황 자세히 보기"
          className="group flex items-start justify-between gap-2 rounded-md border border-purple-200 bg-purple-100/60 px-3 py-2 transition-colors hover:bg-purple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 dark:border-purple-900 dark:bg-purple-900/30 dark:hover:bg-purple-900/50"
        >
          <span className="flex items-center">
            <span aria-hidden="true" className="text-purple-500 mr-2 dark:text-purple-400">
              ✓
            </span>
            <span>
              <strong>25년 3회</strong> 이론 12문제 중{" "}
              <strong className="text-purple-700 dark:text-purple-300">9문제</strong>
            </span>
          </span>
          <span className="shrink-0 self-center whitespace-nowrap text-sm font-semibold text-purple-700 dark:text-purple-300">
            자세히 보기
            <span aria-hidden="true" className="ml-0.5 inline-block transition-transform group-hover:translate-x-0.5">
              &rarr;
            </span>
          </span>
        </Link>
        <Link
          href="/past-exam/2026/2026-1-review"
          aria-label="26년 1회 이론 13문제 중 8문제 적중 — 적중 상황 자세히 보기"
          className="group flex items-start justify-between gap-2 rounded-md border border-purple-200 bg-purple-100/60 px-3 py-2 transition-colors hover:bg-purple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 dark:border-purple-900 dark:bg-purple-900/30 dark:hover:bg-purple-900/50"
        >
          <span className="flex items-center">
            <span aria-hidden="true" className="text-purple-500 mr-2 dark:text-purple-400">
              ✓
            </span>
            <span>
              <strong>26년 1회</strong> 이론 13문제 중{" "}
              <strong className="text-purple-700 dark:text-purple-300">8문제</strong>
            </span>
          </span>
          <span className="shrink-0 self-center whitespace-nowrap text-sm font-semibold text-purple-700 dark:text-purple-300">
            자세히 보기
            <span aria-hidden="true" className="ml-0.5 inline-block transition-transform group-hover:translate-x-0.5">
              &rarr;
            </span>
          </span>
        </Link>
        <Link
          href="/past-exam/2026/2026-2-review"
          aria-label="26년 2회 이론 9문제 중 8문제 적중 — 적중 상황 자세히 보기"
          className="group flex items-start justify-between gap-2 rounded-md border border-purple-200 bg-purple-100/60 px-3 py-2 transition-colors hover:bg-purple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 dark:border-purple-900 dark:bg-purple-900/30 dark:hover:bg-purple-900/50"
        >
          <span className="flex items-center">
            <span aria-hidden="true" className="text-purple-500 mr-2 dark:text-purple-400">
              ✓
            </span>
            <span>
              <strong>26년 2회</strong> 이론 9문제 중{" "}
              <strong className="text-purple-700 dark:text-purple-300">8문제</strong>
            </span>
          </span>
          <span className="shrink-0 self-center whitespace-nowrap text-sm font-semibold text-purple-700 dark:text-purple-300">
            자세히 보기
            <span aria-hidden="true" className="ml-0.5 inline-block transition-transform group-hover:translate-x-0.5">
              &rarr;
            </span>
          </span>
        </Link>
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
          모두 실기 이론 압축 요약 PDF에서 나왔습니다.
        </p>
        <p className="text-purple-700 font-semibold mt-2 dark:text-purple-300">
          26년 2회 기준, 이론 40점 분량의 주제가 수록됐어요.
        </p>
      </div>
    </div>
  );

  // Complete 이용권 안내 배너(JCG-2060) — 풀폭(폴백 본문)/컴팩트(셸 좌측 사이드바)를
  // 공유 컴포넌트(CompleteUpsellBanner)로 통일. 5개 PDF 상세가 동일 소스를 소비한다(drift 방지).
  const completeBanner = <CompleteUpsellBanner variant="full" />;
  const completeBannerCompact = <CompleteUpsellBanner variant="compact" />;

  const recordBannerCompact = (
    <div className="break-words rounded-xl border border-purple-200 bg-purple-50 p-4 dark:border-purple-900 dark:bg-purple-950/40">
      <h2 className="mb-2 text-sm font-semibold text-purple-800 dark:text-purple-300">
        🏆 압축 요약 PDF 적중 실적
      </h2>
      <ul className="space-y-1.5 text-xs text-gray-700 dark:text-gray-300">
        <li className="flex items-start gap-1.5">
          <span aria-hidden="true" className="text-purple-500 dark:text-purple-400">
            ✓
          </span>
          <span>
            <strong>25년 2회</strong> 이론 11문제 중{" "}
            <strong className="text-purple-700 dark:text-purple-300">9문제</strong>
          </span>
        </li>
        <li>
          <Link
            href="/past-exam/2025/2025-3-review"
            aria-label="25년 3회 이론 12문제 중 9문제 적중 — 적중 상황 자세히 보기"
            className="group flex flex-col gap-0.5 rounded-md border border-purple-200 bg-purple-100/60 px-2 py-1.5 transition-colors hover:bg-purple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 dark:border-purple-900 dark:bg-purple-900/30 dark:hover:bg-purple-900/50"
          >
            <span className="flex items-start gap-1.5">
              <span aria-hidden="true" className="text-purple-500 dark:text-purple-400">
                ✓
              </span>
              <span>
                <strong>25년 3회</strong> 이론 12문제 중{" "}
                <strong className="text-purple-700 dark:text-purple-300">9문제</strong>
              </span>
            </span>
            <span className="pl-4 font-semibold text-purple-700 dark:text-purple-300">
              자세히 보기
              <span aria-hidden="true" className="ml-0.5 inline-block transition-transform group-hover:translate-x-0.5">
                &rarr;
              </span>
            </span>
          </Link>
        </li>
        <li>
          <Link
            href="/past-exam/2026/2026-1-review"
            aria-label="26년 1회 이론 13문제 중 8문제 적중 — 적중 상황 자세히 보기"
            className="group flex flex-col gap-0.5 rounded-md border border-purple-200 bg-purple-100/60 px-2 py-1.5 transition-colors hover:bg-purple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 dark:border-purple-900 dark:bg-purple-900/30 dark:hover:bg-purple-900/50"
          >
            <span className="flex items-start gap-1.5">
              <span aria-hidden="true" className="text-purple-500 dark:text-purple-400">
                ✓
              </span>
              <span>
                <strong>26년 1회</strong> 이론 13문제 중{" "}
                <strong className="text-purple-700 dark:text-purple-300">8문제</strong>
              </span>
            </span>
            <span className="pl-4 font-semibold text-purple-700 dark:text-purple-300">
              자세히 보기
              <span aria-hidden="true" className="ml-0.5 inline-block transition-transform group-hover:translate-x-0.5">
                &rarr;
              </span>
            </span>
          </Link>
        </li>
        <li>
          <Link
            href="/past-exam/2026/2026-2-review"
            aria-label="26년 2회 이론 9문제 중 8문제 적중 — 적중 상황 자세히 보기"
            className="group flex flex-col gap-0.5 rounded-md border border-purple-200 bg-purple-100/60 px-2 py-1.5 transition-colors hover:bg-purple-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 dark:border-purple-900 dark:bg-purple-900/30 dark:hover:bg-purple-900/50"
          >
            <span className="flex items-start gap-1.5">
              <span aria-hidden="true" className="text-purple-500 dark:text-purple-400">
                ✓
              </span>
              <span>
                <strong>26년 2회</strong> 이론 9문제 중{" "}
                <strong className="text-purple-700 dark:text-purple-300">8문제</strong>
              </span>
            </span>
            <span className="pl-4 font-semibold text-purple-700 dark:text-purple-300">
              자세히 보기
              <span aria-hidden="true" className="ml-0.5 inline-block transition-transform group-hover:translate-x-0.5">
                &rarr;
              </span>
            </span>
          </Link>
        </li>
      </ul>
      <p className="mt-2 text-xs font-semibold text-purple-700 dark:text-purple-300">
        26년 2회 기준, 이론 40점 분량의 주제가 수록됐어요.
      </p>
    </div>
  );

  // ── 코딩 요약 PDF 크로스셀 배너 ───────────────────────────────────────────
  // 이론 PDF 상세에서 코딩 요약 PDF 상품(/summary-pdf/26-1-coding-compressed)으로 유도.
  // 셸(데스크탑)에선 좌측 사이드바 컴팩트 버전, 폴백(모바일)에선 본문 풀폭 버전("셸=사이드바
  // / 폴백=본문" 분리). Complete(amber)·적중 실적(purple)과 구분되는 blue/indigo 액센트로,
  // 컴팩트는 SectionLevel blue 페어(dark-mode-colors.md §3-2), 풀폭 그라데이션은 cross-mode 예외(§2).
  const codingPdfBannerCompact = (
    <Link
      href="/summary-pdf/26-3-coding-compressed"
      className="block break-words rounded-xl border border-blue-200 bg-blue-50 p-4 transition-all hover:bg-blue-100 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:border-blue-900 dark:bg-blue-950/40 dark:hover:bg-blue-950/60"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-bold text-blue-800 dark:text-blue-300">
          💻 실기 코딩 요약 PDF
        </h2>
        <span aria-hidden="true" className="shrink-0 text-lg text-blue-600 dark:text-blue-400">
          &rarr;
        </span>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-blue-700 dark:text-blue-200">
        C·Java·Python·SQL 기출 코드 패턴을 압축 정리한 2026년 3회 실기 코딩 요약 PDF도 함께 학습하세요!
      </p>
    </Link>
  );

  const codingPdfBanner = (
    <Link
      href="/summary-pdf/26-3-coding-compressed"
      className="block bg-gradient-to-r from-indigo-500 to-blue-500 p-6 rounded-lg text-white hover:from-indigo-600 hover:to-blue-600 transition-all hover:shadow-lg"
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold mb-1">💻 실기 코딩 요약 PDF</h3>
          <p className="text-sm text-blue-100">
            C·Java·Python·SQL 기출 코드 패턴을 압축 정리한 2026년 3회 실기 코딩 요약 PDF도 함께 학습하세요!
          </p>
        </div>
        <div className="text-2xl ml-4" aria-hidden="true">
          &rarr;
        </div>
      </div>
    </Link>
  );

  // 좌측 사이드바 노드(셸 데스크탑 전용) — Complete 안내 + 코딩 PDF 크로스셀 + 적중 실적 세로 스택.
  const leftAside = (
    <div className="flex flex-col gap-4">
      {completeBannerCompact}
      {codingPdfBannerCompact}
      {recordBannerCompact}
    </div>
  );

  const perksBlock = (
    <div className="bg-orange-50 p-6 rounded-lg border-l-4 border-orange-500">
      <h3 className="text-lg font-semibold text-orange-800 mb-3">
        📘 시험 직전 핵심 압축
      </h3>
      <p className="text-gray-700 text-sm leading-relaxed">
        시험에 자주 나오는 <strong className="text-orange-700">핵심만 압축 정리</strong>한 요약본이에요. 인쇄해서 막판 점검용으로 활용하세요.
      </p>
    </div>
  );

  const reviewsBlock = (
    <div className="bg-amber-50 p-6 rounded-lg border-l-4 border-amber-500">
      <h3 className="text-lg font-semibold text-amber-800 mb-4">
        💬 25년 3회 실기 시험을 본 분들의 반응
      </h3>
      <p className="text-gray-600 text-sm mb-4">
        정처기 감자를 이용하고 2025년 3회 실기 시험을 봤던 분들의
        실제 반응입니다.
      </p>

      {/* 유튜브 댓글 */}
      <div className="mb-6">
        <p className="text-amber-700 font-medium text-sm mb-3 flex items-center">
          <span className="mr-2">📺</span> 유튜브 댓글
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          {[1, 2, 3, 4].map((num) => (
            <div
              key={`youtube-${num}`}
              className="rounded-lg overflow-hidden shadow-sm border border-amber-200"
            >
              <Image
                src={`/summary-pdf/26-1-compressed/review/youtube${num}.webp`}
                alt={`유튜브 댓글 ${num}`}
                width={400}
                height={200}
                className="w-full h-auto"
              />
            </div>
          ))}
        </div>
      </div>

      {/* 정처기 감자 댓글 */}
      <div className="mb-6">
        <p className="text-amber-700 font-medium text-sm mb-3 flex items-center">
          <span className="mr-2">🥔</span> 정처기 감자 댓글
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          {[1, 2, 3, 4].map((num) => (
            <div
              key={`comment-${num}`}
              className="rounded-lg overflow-hidden shadow-sm border border-amber-200"
            >
              <Image
                src={`/summary-pdf/26-1-compressed/review/comment${num}.webp`}
                alt={`정처기 감자 댓글 ${num}`}
                width={400}
                height={200}
                className="w-full h-auto"
              />
            </div>
          ))}
          {[5, 6].map((num) => (
            <div
              key={`comment-${num}`}
              className="rounded-lg overflow-hidden shadow-sm border border-amber-200"
            >
              <Image
                src={`/summary-pdf/26-1-compressed/review/2/comment${num}.webp`}
                alt={`정처기 감자 댓글 ${num}`}
                width={400}
                height={200}
                className="w-full h-auto"
              />
            </div>
          ))}
        </div>
      </div>

      {/* 인스타그램 */}
      <div className="mb-6">
        <p className="text-amber-700 font-medium text-sm mb-3 flex items-center">
          <span className="mr-2">📸</span> 인스타그램
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          {[1, 2].map((num) => (
            <div
              key={`insta-${num}`}
              className="rounded-lg overflow-hidden shadow-sm border border-amber-200"
            >
              <Image
                src={`/summary-pdf/26-1-compressed/review/2/insta${num}.webp`}
                alt={`인스타그램 ${num}`}
                width={400}
                height={200}
                className="w-full h-auto"
              />
            </div>
          ))}
        </div>
      </div>

      {/* 카카오톡 */}
      <div>
        <p className="text-amber-700 font-medium text-sm mb-3 flex items-center">
          <span className="mr-2">💬</span> 카카오톡
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          <div className="rounded-lg overflow-hidden shadow-sm border border-amber-200">
            <Image
              src="/summary-pdf/26-1-compressed/review/2/kakao1.webp"
              alt="카카오톡 1"
              width={400}
              height={83}
              className="w-full h-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const contentsBlock = (
    <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
      <h3 className="text-lg font-semibold text-blue-800 mb-4">
        📚 PDF 구성 내용
      </h3>
      <ul className="space-y-2 text-gray-700">
        <li className="flex items-start">
          <span className="text-blue-500 mr-2">•</span>
          <span>기출문제에 나온 키워드를 반영한 요약 PDF</span>
        </li>
        <li className="flex items-start">
          <span className="text-blue-500 mr-2">•</span>
          <span>2026년 2회까지의 기출문제 반영 완료</span>
        </li>
        <li className="flex items-start">
          <span className="text-blue-500 mr-2">•</span>
          <span>
            기출문제 분석으로 핵심 중에 핵심만 모은 압축 요약 PDF
          </span>
        </li>
      </ul>
    </div>
  );

  const featuresBlock = (
    <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
      <h3 className="text-lg font-semibold text-green-800 mb-4">
        🎉 주요 특징
      </h3>
      <div className="space-y-4">
        <div>
          <h4 className="font-semibold text-gray-800 mb-2">
            1. 손쉽게 PDF 내용을 외웠는지 확인할 수 있는 가림막
            추가 PDF 제공
          </h4>
          <Image
            src="/summary-pdf/26-1-compressed/3d_compressed-with-djc_hide_gamja.webp"
            alt="가림막 감자 캐릭터"
            width={120}
            height={120}
            className="w-[80px] h-auto mx-auto my-2"
          />
          <p className="text-gray-700 text-sm leading-relaxed">
            Q버전은 시험에 출제되는 주요 키워드가 가려져
            있어서 요약 PDF를 제대로 공부했는지 확인할 수
            있습니다. 가림막을 클릭하면 키워드가 보이며 편하게
            공부할 수 있습니다. <strong>v1-0 Q버전도 제공됩니다.</strong>
          </p>

          <Image
            src="/summary-pdf/25-3-compressed/q-version.gif"
            alt="가림막 버전 데모"
            width={500}
            height={400}
            className="w-[400px] h-auto mx-auto my-4"
          />
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 my-3">
            <p className="text-yellow-800 text-xs font-medium mb-1">
              ⚠️ 주의사항
            </p>
            <ul className="text-yellow-700 text-xs space-y-1">
              <li>
                •{" "}
                <a
                  href="https://get.adobe.com/reader/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Adobe Acrobat Reader (클릭 시 다운로드)
                </a>{" "}
                데스크톱 앱을 권장합니다.
                <br />
                (Chrome 등 브라우저에서는 뷰어에 따라 동작이 달라질 수 있습니다.)
              </li>
              <li>
                • 맥 미리보기·모바일 PDF 앱에서는 가림막이 열리지 않습니다. 태블릿·모바일에서는 구매 목록의 “웹에서 보기”를 이용해 주세요.
              </li>
            </ul>
          </div>
        </div>
        <hr className="my-4" />
        <div>
          <h4 className="font-semibold text-gray-800 mb-2">
            2. 태블릿 유저를 위한 가로 버전 제공
          </h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            H버전은 태블릿 유저를 위한 가로 버전으로
            제공됩니다. 가로 버전은 세로버전에 비해 느슨한
            레이아웃으로 제공되어 필기에 적합합니다. <strong>v1-0 H버전도 제공됩니다.</strong>
          </p>
          <Image
            src="/summary-pdf/26-3-compressed/v1-0/8e5b8b63/h.webp"
            alt="가로 버전 데모"
            width={800}
            height={568}
            className="w-[800px] h-auto mx-auto my-4"
          />
        </div>
        <hr className="my-4" />
        <div>
          <h4 className="font-semibold text-gray-800 mb-2">
            3. 막히는 즉시 해소! &apos;바로가기&apos; 하이퍼링크
            🔗➡🥔 기능 탑재
          </h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            더 이상 요약본을 보다가 이해 안 되는 부분을 따로
            검색할 필요가 없습니다. PDF에서 궁금한 키워드 바로 옆
            하이퍼링크를 클릭하면 1초 만에 &apos;정처기
            감자&apos;의 이론 설명 페이지로 이동하여 학습하고
            문제를 풀 수 있습니다.
          </p>
          <Image
            src="/summary-pdf/25-3-compressed/output1.gif"
            alt="하이퍼링크 데모"
            width={500}
            height={258}
            className="w-[500px] h-auto mx-auto my-4"
          />
        </div>
        <hr className="my-4" />
        <div>
          <h4 className="font-semibold text-gray-800 mb-2">
            4. 한 번의 클릭으로 원하는 곳 이동! PDF 책갈피 기능
            추가
          </h4>
          <p className="text-gray-700 text-sm leading-relaxed">
            PDF 뷰어(Acrobat Reader, 크롬 등) 패널에 PDF의 상세
            목차가 구성되어, 더 이상 페이지를 넘기거나 스크롤할
            필요 없이 원하는 챕터나 소제목으로 단 한 번의
            클릭만으로 이동할 수 있습니다.
          </p>
          <Image
            src="/summary-pdf/25-3-compressed/output2.gif"
            alt="책갈피 데모"
            width={600}
            height={536}
            className="w-[500px] h-auto mx-auto my-4"
          />
        </div>
      </div>
    </div>
  );

  const downloadBlock = (
    <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-gray-400">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        📥 업데이트된 PDF 다운로드 방법
      </h3>
      <p className="text-gray-700 text-sm leading-relaxed mb-2">
        <strong>
          내 메뉴(/my) {">"} 구매 목록 {">"} 다운로드 버튼
        </strong>
        을 클릭하면 최신 버전이 다운로드됩니다.
      </p>
      <p className="text-gray-700 text-sm leading-relaxed">
        <strong>버전별로 다운 가능</strong> → 최신 버전이 마음에
        들지 않는다면 다시 기존 버전을 재 다운로드 받을 수
        있습니다.
      </p>
      <Image
        src="/summary-pdf/25-3-compressed/download.webp"
        alt="다운로드 데모"
        width={800}
        height={538}
        className="w-[500px] h-auto mx-auto my-4"
      />
    </div>
  );

  // 셸 가운데 본문 — 캐러셀 + 세일즈 상세. "Complete 이용권 안내"·"적중 실적"(recordBlock/
  // completeBanner)은 좌측 사이드바(leftAside)로 이동했으므로 본문에선 제외(중복 방지).
  // 섹션 앵커 네비 제거로 <section id>·scroll-mt 도 단순 div 로 정리.
  const shellBody = (
    <>
      <div className="mb-10">
        <div className="[&>div]:lg:w-full">
          <ProductCarousel images={product.images} />
        </div>
      </div>

      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-foreground">상품 설명</h2>
        <div className="prose max-w-none space-y-6">{overviewInner}</div>
      </div>

      <div className="prose max-w-none">
        <div className="mt-8 space-y-6">
          {perksBlock}
          {reviewsBlock}
          {contentsBlock}
          {featuresBlock}
          {downloadBlock}
        </div>
      </div>
    </>
  );

  // 셸 밖(모바일/태블릿/SSR) 폴백 — 기존 클래식 풀블리드 랜딩(레이아웃·결제 위치 byte-identical).
  const fallback = (
    <div className="flex flex-col w-screen relative left-1/2 transform -translate-x-1/2">
      <main className="flex-1 px-4 py-8">
        {/* 위쪽: 상품 정보 */}
        <div className="flex justify-center mb-12">
          <div className="w-full max-w-[1000px] px-4">
            <div className="flex flex-col lg:flex-row gap-2 lg:gap-15">
              {/* 상품 이미지 Carousel */}
              <ProductCarousel images={product.images} />

              {/* 상품 정보 및 결제 */}
              {productInfo}
            </div>
          </div>
        </div>

        {/* 아래쪽: 상품 설명 */}
        <div className="max-w-[700px] mx-auto px-4">
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-foreground">상품 설명</h2>
            <div className="prose max-w-none">
              {overviewInner}

              {/* 상세 설명 */}
              <div className="mt-8 space-y-6">
                {recordBlock}
                {completeBanner}
                {codingPdfBanner}
                {perksBlock}
                {reviewsBlock}
                {contentsBlock}
                {featuresBlock}
                {downloadBlock}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );

  return (
    <>
      <Script src={process.env.NEXT_PUBLIC_NICEPAY_JS_SDK_URL}></Script>
      <Script src="https://accounts.google.com/gsi/client" />
      <PdfDetailShell leftAside={leftAside} cta={productInfo} fallback={fallback}>
        {shellBody}
      </PdfDetailShell>
    </>
  );
}
